import { useState, useRef, useCallback, useEffect } from "react";
import * as tf from "@tensorflow/tfjs";
import Webcam from "react-webcam";
import { useSelector, useDispatch } from "react-redux";
import {
  setInferenceTime,
  setIsLoading,
  setIsDetecting,
  setDetectionImage,
  setNumBoxes,
} from "../features/detectSlice";
import {setLogout} from "../features/authSlice";
import useCam from "../hooks/useCam";
import { color_map, processTfjsYOLOOutput } from "../yolo_tf";
import useLocation from "../hooks/useLocation";
// import useModel from "../hooks/useModel";
import { useDetect } from "../contexts/DetectContext";
import SwitchButton from "./SwitchButton";
import MultiSelect from "./MultiSelect";
import style from "./VideoDetection.module.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";

function VideoDetection({
  isCollecting = false,
  isDisplay = true,
  setImageCount,
  activeButton = "Container",
  setActiveButton,
}) {
  const dispatch = useDispatch();
  const {token,user} = useSelector((state) => state.auth);
  // console.log("User:", user);
  const isAdmin = user.role === "admin";
  const { cityName } = useSelector((state) => state.model);
  const { model } = useDetect();

  // useModel(cityName, modelSize);
  const { isDetecting } = useSelector((state) => state.detection);
  const isContainer = activeButton === "Container";

  const { devices, selectedDeviceId, setSelectedDeviceId, isPortrait } =
    useCam();
  const numDevices = devices.length;

  const [isCamActive, setIsCamActive] = useState(false);
  const [camMessage, setIsCamMessage] = useState("");
  const [secDelay, setSecDelay] = useState(3);

  const { fetchLocation } = useLocation();
  const { location } = useSelector((state) => state.detection);
  const locationRef = useRef(location);
  const city = useRef(cityName);

  // console.log("Component:", location);
  const navigate = useNavigate();
  // console.log(`process each ${secDelay} sec`)
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const OrgcanvasRef = useRef(null);
  const intervalRef = useRef(null);

  const startDetection = () => {
    dispatch(setIsDetecting(true));
    processFrames();
  };

  const stopDetection = useCallback(() => {
    clearInterval(intervalRef.current);
    dispatch(setIsDetecting(false));
    dispatch(setDetectionImage(null));
    dispatch(setInferenceTime(0));
  }, [dispatch, intervalRef]); //setIsDetecting, setDetectionImage,setInferenceTime

  useEffect(() => {
    return () => {
      stopDetection();
    };
  }, [stopDetection]);

  useEffect(() => {
    fetchLocation();
  }, [fetchLocation]);

  useEffect(() => {
    locationRef.current = location; // Update the ref whenever location changes
    city.current = cityName;
    // console.log("Ref", locationRef.current);
  }, [location,cityName]);

  const sendImageToServer = async (imageDataUrl) => {
    const urlEndpoint = isContainer ? "/upload-image2" : "/upload-data"; // Replace with your server endpoint URL"/upload-image"; // Replace with your server endpoint URL
    if (location.latitude === -1 || location.longitude === -1) {
      console.log("Location not available");
      return;
    }
    // console.log("Send:", locationRef.current);

    try {
      const formData = new FormData();
      const blob = await (await fetch(imageDataUrl)).blob();
      formData.append("Latitude", locationRef.current.latitude);
      formData.append("Longitude", locationRef.current.longitude);
      // formData.append("Userid", 14);
      // formData.append("Accesstoken", '5566ehdd77636');
      formData.append("image", blob, "image.png");
      formData.append("city", city.current);
      // formData.append("City", 1);
      // console.log(token)
      // console.log(formData)
      //for containers => upload-image2
      // for data => upload-data
      const response = await fetch(
        "https://api.up2smart.com:5001" + urlEndpoint,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`, // Add the Authorization header
          },
          body: formData,
        }
      );
      if (!response.ok) {
        if (response.status===401) {
          toast.error("Unauthorized access. Please login again.");
          dispatch(setLogout());
          stopDetection();
          navigate("/login");
          return; // Stop further execution
        }
        throw new Error("Failed to send image to server");
      }
      if (response.ok) {
        // const data = await response.json();
        setImageCount((count) => count + 1);
        // console.log("Image sent to server:", data.message);
      }
      
    } catch (error) {
      console.error("Error sending image to server:", error);
      toast.error("Error sending image to server");
      stopDetection();}
  };

  const processFrames = async () => {
    if (isContainer && !model) return;
    dispatch(setIsLoading(true));

    const video = webcamRef.current?.video;
    const OrgCanvas = OrgcanvasRef.current;
    const canvas = canvasRef.current;
    if (!video || !OrgcanvasRef) return;

    const cto = OrgCanvas.getContext("2d", { willReadFrequently: true });
    OrgCanvas.width = video.videoWidth;
    OrgCanvas.height = video.videoHeight;
    // console.log("Video Width:", OrgCanvas.width);
    // console.log("Video Height:", video.videoHeight);
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    canvas.width = 640;
    canvas.height = 640;
    // console.log("Video Width:", canvas.width);
    // console.log("Video Height:", video.videoHeight);
    const processFrame = async () => {
      // if (!isDetecting) return;

      const startTime = performance.now();

      cto.clearRect(0, 0, OrgCanvas.width, OrgCanvas.height);
      cto.drawImage(video, 0, 0, OrgCanvas.width, OrgCanvas.height);

      fetchLocation();
      // console.log("Process:", locationRef.current);
      // console.log(activeButton,isContainer)
      if (!isContainer) {
        dispatch(setIsLoading(false));
        // console.log("Not a container")

        await sendImageToServer(OrgCanvas.toDataURL());
        return;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const inputTensor = tf.tidy(() => {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        return tf.browser.fromPixels(imageData).expandDims(0).div(255.0);
      });

      // const resizedTensor = tf.image.resizeBilinear(inputTensor, [640, 640]);
      // const originalImageDataUrl = webcamRef.current.getScreenshot()
      // alert(canvas.toDataURL().slice(0, 100));
      // const output = await model.execute(inputTensor);

      const output = model.predict(inputTensor);
      const boxes = processTfjsYOLOOutput(output, 0.7);

      let yolo_classes = [];

      if (cityName === "Tarragona") {
        yolo_classes = ["container"];
      } else {
        yolo_classes = ["Rebuig", "Organic", "Vindre", "Envasos", "Paper"];
      }
      // console.log(cityName)
      // console.log(yolo_classes)
      dispatch(setNumBoxes(boxes.length));
      if (boxes.length > 0 && !isDisplay) {
        await sendImageToServer(OrgCanvas.toDataURL());
      }
      if (boxes.length > 0 && isDisplay) {
        // Draw bounding boxes on the image
        boxes.forEach((box) => {
          const text = `${yolo_classes[box.class]}: ${(
            box.confidence * 100
          ).toFixed(1)}%`;
          const textWidth = ctx.measureText(text).width; // Measure the text width
          const padding = 4; // Padding for the rectangle
          const textHeight = 18; // Height of the text (font size)
          const [x1, y1, x2, y2] = box.bbox;
          // ctx.strokeStyle = "red";
          ctx.strokeStyle = color_map[box.class];
          ctx.lineWidth = 2;
          ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

          ctx.fillStyle = color_map[box.class];
          ctx.fillRect(
            x1,
            y1 > 20 ? y1 - textHeight - padding : y1 + 20,
            textWidth + padding * 2,
            textHeight + padding
          );

          ctx.fillStyle = box.class === 3 ? "black" : "white";
          ctx.font = "16px Arial";
          ctx.fillText(
            text,
            x1 + padding, // Add padding to the text
            y1 > 20 ? y1 - padding : y1 + 20 + textHeight
          );
        });
        // Pass the processed image to the parent
        // await sendImageToServer(OrgCanvas.toDataURL());
      }

      tf.dispose([inputTensor, output, boxes]);
      isDisplay && dispatch(setDetectionImage(canvas.toDataURL()));
      dispatch(setIsLoading(false));
      const endTime = performance.now();
      dispatch(setInferenceTime((endTime - startTime).toFixed(2)));
    };
    // console.log(`process each ${secDelay} sec`);
    intervalRef.current = setInterval(processFrame, secDelay * 1000);
  };

  const handleSwitchCamera = () => {
    const index = devices.findIndex(
      (device) => device.deviceId === selectedDeviceId
    );
    const nextIndex = (index + 1) % devices.length;
    setSelectedDeviceId(devices[nextIndex].deviceId);
    // alert(`Switched to Camera ${nextIndex+1}`);
  };
  const handleUserMedia = () => {
    setIsCamActive(true);
    setIsCamMessage("Webcam feed is active");
  };
  const handleUserMediaError = (error) => {
    setIsCamMessage(`🚫 Error accessing the camera 😢`);
    setIsCamActive(false);
  };

  return (
    <div className={style.imageUpload}>
      <h2>Inspection Mode</h2>
      {/* {isDetecting && location !== null && (
        <p>
          🌎 Lat: {location.latitude} || Lon: {location.longitude}
        </p>
      )} */}
      {numDevices < 1 && (
        <p
          className={style.cameraError}
          style={{ fontSize: "20px", fontWeight: "700", color: "red" }}
        >
          🚫 No camera devices found 😢
        </p>
      )}
      {numDevices > 0 && (
        <p
          className={style.cameraError}
          style={{ fontSize: "20px", fontWeight: "700", color: "red" }}
        >
          {camMessage}
        </p>
      )}
      {isAdmin && !isDetecting && isCollecting && numDevices > 0 && (
        <SwitchButton
          activeButton={activeButton}
          setActiveButton={setActiveButton}
        />
      )}
      {isAdmin && !isDetecting ? (
        <MultiSelect secDelay={secDelay} setSecDelay={setSecDelay} />
      ) : (
        <>
          <p className={style.delay}>{`You are in ${
            isContainer ? "Detect" : "Collect"
          } Mode`}</p>
          <p className={style.delay}>🕓 Delay : {secDelay} sec</p>
        </>
      )}
      <div className="imageContainer">
        {numDevices > 1 && (
          <button onClick={handleSwitchCamera} className="cameraBtn">
            <img
              src={`${process.env.PUBLIC_URL}/images/switch-camera.png`}
              alt="change camera"
            />
          </button>
        )}
        {!(numDevices < 1) && (
          <>
            <canvas ref={canvasRef} style={{ display: "none" }} />
            <canvas ref={OrgcanvasRef} style={{ display: "none" }} />
            <Webcam
              ref={webcamRef}
              videoConstraints={{
                deviceId: selectedDeviceId,
                width: { ideal: isPortrait ? 1080 : 1920 },
                height: { ideal: isPortrait ? 1920 : 1080 },
              }}
              onUserMedia={handleUserMedia}
              onUserMediaError={handleUserMediaError}
            />
            {isCamActive ? (
              !isDetecting ? (
                <button className={style.detectBtn} onClick={startDetection}>
                  {/* {" "} */}
                  <span className={style.openCam}></span>
                </button>
              ) : (
                <button className={style.detectBtn} onClick={stopDetection}>
                  {/* {" "} */}
                  <span className={style.closeCam}></span>
                </button>
              )
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export default VideoDetection;
